import { GridPaginationModel, GridSortModel } from '@mui/x-data-grid';
import { ChangeEvent, useCallback, useEffect, useMemo } from 'react';
import { SelectOption } from 'src/UIKit';
import { useApplicationsService } from 'src/modules/applications/services';
import { Application } from 'src/modules/applications/types';
import { RootState } from 'src/store';
import { debounce } from 'ts-debounce';
import { usePlanService, usePlansService } from '../services';
import { PlanFormData } from '../types';
import { useCreateAdminPlanFormDrawerController } from './CreateAdminPlanFormDrawerController';

export const useAdminPlansViewController = () => {
  const {
    loading: applicationsLoading,
    list: applicationList,
    getApplications,
    changeApplicationsFilter,
    resetApplications,
  } = useApplicationsService();
  const {
    loading: plansLoading,
    filter: plansFilter,
    list: planList,
    getPlans,
    changePlansFilter,
    resetPlans,
  } = usePlansService();
  const { archivePlan, unarchivePlan } = usePlanService();

  const createAdminPlanFormDrawerController = useCreateAdminPlanFormDrawerController();

  const initLoad = useCallback(async () => {
    try {
      changeApplicationsFilter({ limit: 100 });
      const response = await getApplications();

      if (response.items.length) {
        changePlansFilter({
          applicationId: response.items[0].id,
          types: 'private,public',
          statuses: 'active,archived',
        });
        await getPlans();
      }
    } catch (e) {
      console.log(e);
    }
  }, []);

  const reloadPlans = useCallback(async () => {
    try {
      await getPlans();
    } catch (e) {
      console.log(e);
    }
  }, []);

  const reloadPlansDebounce = useMemo(() => debounce(reloadPlans, 500), []);

  const changePlanFilterForm = useCallback(
    async (filter: Partial<RootState['plans']['filter']>) => {
      changePlansFilter(filter);
      await reloadPlans();
    },
    [],
  );

  const changePlansSearchFilter = useCallback(
    async (_: ChangeEvent<HTMLInputElement>, query: string) => {
      changePlansFilter({ query });
      await reloadPlansDebounce();
    },
    [],
  );

  const changePlansSorting = useCallback(async (s: GridSortModel) => {
    changePlansFilter({ sort: `${s[0].field}_${s[0].sort}` });
    await reloadPlans();
  }, []);

  const changePlansPagination = useCallback(async (p: GridPaginationModel) => {
    changePlansFilter({ page: p.page, limit: p.pageSize, offset: p.page * p.pageSize });
    await reloadPlans();
  }, []);

  const archiveAdminPlan = useCallback(async (planId: string) => {
    try {
      await archivePlan(planId);
      await getPlans();
    } catch (e) {
      console.log(e);
    }
  }, []);

  const unarchiveAdminPlan = useCallback(async (planId: string) => {
    try {
      await unarchivePlan(planId);
      await getPlans();
    } catch (e) {
      console.log(e);
    }
  }, []);

  const applicationOptions: SelectOption<string, string, Application>[] = useMemo(() => {
    return (
      applicationList?.items?.map(a => ({
        value: a.id,
        title: a.name,
        entity: a,
      })) || []
    );
  }, [applicationList]);

  useEffect(() => {
    initLoad();

    return () => {
      resetApplications();
      resetPlans();
    };
  }, []);

  return {
    isPageLoading: applicationsLoading.getList || plansLoading.getList,
    plansFilter,
    planList,
    archiveAdminPlan,
    unarchiveAdminPlan,
    changePlanFilterForm,
    changePlansSearchFilter,
    changePlansSorting,
    changePlansPagination,
    applicationOptions,
    createAdminPlanFormDrawerController: {
      ...createAdminPlanFormDrawerController,
      submit: (full: PlanFormData, dirty: Partial<PlanFormData>) =>
        createAdminPlanFormDrawerController.submit(full, dirty, getPlans),
    },
  };
};
